import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import EditActionButton from '../../../../components/designe/ActionButtons/EditActionButton';
import AppUserAvatar from '../../../../components/designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import AppButton from '../../../../components/designe/buttons/app-button/AppButton';
import { useRegisterPageContainerError } from '../../../../components/layout/page-container/page-container-error-handler';
import ConfirmDialog from '../../../../components/reusable/confirm-dialog/ConfirmDialog';
import UserMultiSelectDialog from '../../../../components/reusable/user-multi-select-dialog/UserMultiSelectDialog';
import { AvatarSize, AvatarTypes } from '../../../../shared/constants';
import type { IUserTrainingAccessSectionKey } from '../../../../types/interfaces/user-training-access';
import { getUserViewName } from '../../../../utils/common/shared-util';
import { useStyles } from './AccessSettings.styles';
import { useAccessSettings } from './useAccessSettings';

const SECTIONS: {
  key: IUserTrainingAccessSectionKey;
  labelKey: string;
}[] = [
  { key: 'createTraining', labelKey: 'settings.accessCreateTraining' },
  { key: 'shareTrainings', labelKey: 'settings.accessShareTrainings' },
  { key: 'sharePrograms', labelKey: 'settings.accessSharePrograms' },
];

export default function AccessSettings() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);

  const {
    isLoading,
    isError,
    errors,
    isPosting,
    refetch,
    rowsBySection,
    displayUsersBySection,
    modalUsersBySection,
    dialogSection,
    openDialog,
    closeDialog,
    handleModalSave,
    removeTarget,
    setRemoveTarget,
    handleConfirmRevoke,
  } = useAccessSettings();

  useRegisterPageContainerError({
    errors,
    isLoading,
    hasData: !isError && !isLoading,
    onRetry: refetch,
  });

  const dialogTitle = useMemo(() => {
    if (!dialogSection) {
      return '';
    }
    const item = SECTIONS.find((s) => s.key === dialogSection);
    return item ? t(item.labelKey) : '';
  }, [dialogSection, t]);

  return (
    <>
      <div className={classes.wrapper}>
        {isLoading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress />
          </Box>
        ) : null}

        {isError && !isLoading ? (
          <Box className={classes.errorBox}>
            <Typography color="error">{t('common.error')}</Typography>
            <AppButton onClick={() => refetch()}>{t('common.retry')}</AppButton>
          </Box>
        ) : null}

        {!isLoading && !isError
          ? SECTIONS.map(({ key, labelKey }) => (
              <Box key={key}>
                <Box className={classes.sectionHeader}>
                  <Typography
                    variant="label-text-md"
                    className={classes.sectionLabel}>
                    {t(labelKey)}
                  </Typography>
                  <EditActionButton onClick={() => openDialog(key)} />
                </Box>
                <Box className={classes.chips}>
                  {displayUsersBySection[key].length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('common.noDataToDisplayMessage')}
                    </Typography>
                  ) : (
                    displayUsersBySection[key].map((user) => (
                      <Chip
                        key={user.id}
                        className={classes.chip}
                        avatar={
                          <AppUserAvatar
                            entity={user}
                            size={AvatarSize.xs}
                            type={AvatarTypes.round}
                          />
                        }
                        label={getUserViewName(user)}
                        onDelete={() => setRemoveTarget({ section: key, user })}
                      />
                    ))
                  )}
                </Box>
              </Box>
            ))
          : null}
      </div>

      <UserMultiSelectDialog
        open={dialogSection !== null}
        onClose={closeDialog}
        title={dialogTitle}
        subtitle={t('settings.accessModalSubtitle')}
        users={dialogSection ? modalUsersBySection[dialogSection] : []}
        selectedUserIds={
          dialogSection ? rowsBySection[dialogSection].map((r) => r.id) : []
        }
        onSave={(ids) => {
          if (dialogSection) {
            void handleModalSave(dialogSection, ids);
          }
        }}
        isSaving={isPosting && dialogSection !== null}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title={t('settings.accessRevokeTitle')}
        onSubmit={() => void handleConfirmRevoke()}
        isLoading={isPosting && removeTarget !== null}>
        {removeTarget
          ? t('settings.accessRevokeMessage', {
              name: getUserViewName(removeTarget.user),
            })
          : null}
      </ConfirmDialog>
    </>
  );
}
